import React, { useEffect, useRef, useState } from 'react';
import { createProperty, listProperties, PropertiesResponse, PropertyCreated } from '../../../../api/stays-properties-client';
import type { PropertyLayout } from '../../../../api/stays-properties-client';
import { StaysApiError } from '../../../../api/stays-client';
import { AppButton } from '../../../button/app-button';
import { Stepper } from '../../../stepper/stepper';
import { Field, FieldError, FieldHint, FieldRow, Input, Label, Select } from '../../../form-kit';
import { FormSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import {
  BuildingLocation,
  BuildingLocationPicker,
  EMPTY_LOCATION,
} from '../../dashboard/dashboard-buildings-components/building-location-picker';
import { useBuildingZone } from '../../dashboard/dashboard-buildings-components/building-time-zone';
import { refusalText, STAYS_COUNTRIES } from '../../dashboard/dashboard-stays-components/stays-labels';
import { LAYOUT_WORDS } from '../../../../stays/wording';
import {
  buildCreateInput,
  EMPTY_FORM,
  EMPTY_ROOM_TYPE,
  HOTEL_MAX,
  HOTEL_MIN,
  hotelRoomCount,
  ROOM_TYPE_NAME_MAX,
  ROOM_TYPES_MAX,
  RoomTypeDraft,
  splitRoomList,
  LABEL_MAX,
  labelsFor,
  mapFieldErrors,
  MULTI_MAX,
  PropertyForm,
  splitPastedList,
} from './properties-model';
import {
  Form,
  Inline,
  KindCard,
  KindGroup,
  KindHint,
  KindTitle,
  PasteArea,
  RowLine,
  Rows,
  Section,
  SectionTitle,
  Small,
  Statement,
} from './properties.styled';

const LAYOUTS: PropertyLayout[] = ['whole', 'apartment', 'multi', 'hotel'];

/**
 * ADD A PROPERTY (Independent Stays IS2) — step 1 of the setup guide for an
 * organisation without Lobbero Desk (kickoff decision: a step of ISR's
 * wizard, not a separate flow). What kind of place it is, where it is (the
 * map pin sets its time zone), its apartments, what guests see, and the host's
 * short-term rental statement (IS-Q16). Underneath, the server makes the same
 * building, units and listings a Lobbero customer has.
 *
 * The statement's words and whether it is asked come from the server
 * (GET /stays/properties → attestation), so the stored wording version is
 * always the text the host ticked.
 */
export const AddPropertyForm: React.FC<{ onCreated: (r: PropertyCreated) => void }> = ({ onCreated }) => {
  const [meta, setMeta] = useState<PropertiesResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [form, setForm] = useState<PropertyForm>(EMPTY_FORM);
  const [location, setLocation] = useState<BuildingLocation>(EMPTY_LOCATION);
  const [unresolved, setUnresolved] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState('');
  const zone = useBuildingZone({ savedZone: '' });
  const typedAddress = useRef(false);
  const typedCity = useRef(false);
  const pickedCountry = useRef(false);

  const load = () => {
    setFailed(false);
    listProperties()
      .then(setMeta)
      .catch(() => setFailed(true));
  };
  useEffect(load, []);

  // A picked place fills the address and town unless the host typed their own.
  useEffect(() => {
    setForm(f => ({
      ...f,
      address: typedAddress.current ? f.address : location.address || location.formatted_address || f.address,
      city: typedCity.current ? f.city : location.city || f.city,
    }));
  }, [location.address, location.formatted_address, location.city]);

  if (failed) return <LoadError message="Could not open the property form." onRetry={load} />;
  if (!meta) return <FormSkeleton fields={5} label="the property form" />;

  const required = meta.attestation.mode === 'required';
  const rows = labelsFor(form.layout, form.labels);
  const set = (patch: Partial<PropertyForm>) => setForm(f => ({ ...f, ...patch }));
  const setLabel = (i: number, v: string) =>
    setForm(f => {
      const next = labelsFor(f.layout, f.labels).slice();
      next[i] = v;

      return { ...f, labels: next };
    });
  const eid = (k: string) => `property-${k.replace(/\./g, '-')}-error`;
  const room = meta.capacity.remaining;
  const hotel = form.layout === 'hotel';
  const roomsInAll = hotelRoomCount(form.roomTypes);
  const wants = form.layout === 'multi' ? rows.length : hotel ? roomsInAll : 1;
  const setType = (i: number, patch: Partial<RoomTypeDraft>) =>
    setForm(f => ({ ...f, roomTypes: f.roomTypes.map((rt, j) => (j === i ? { ...rt, ...patch } : rt)) }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const local: Record<string, string> = {};
    if (!form.layout) local.layout = 'Choose what kind of place it is.';
    if (hotel) {
      form.roomTypes.forEach((rt, i) => {
        if (!rt.name.trim()) local[`room_types.${i}.name`] = 'Name this room type, for example Double.';
        if (!splitRoomList(rt.rooms).length) local[`room_types.${i}.rooms`] = 'Add at least one room number.';
      });
      if (!Object.keys(local).length && (roomsInAll < HOTEL_MIN || roomsInAll > HOTEL_MAX)) {
        local.room_types = `A hotel has ${HOTEL_MIN} to ${HOTEL_MAX} rooms in all.`;
      }
    }
    if (unresolved) local.location = 'Pick the address from the list, or clear the search.';
    if (required && !form.attest) local.attest = 'Tick the box to confirm you are allowed to rent this property for short stays.';
    if (Object.keys(local).length) {
      setErrors(local);

      return;
    }
    setSaving(true);
    setErrors({});
    setProblem(null);
    try {
      const out = await createProperty(buildCreateInput(form, location, zone.zone, required));
      onCreated(out);
    } catch (err) {
      const se = err instanceof StaysApiError ? err : null;
      if (se && se.code === 'INVALID_FIELDS') setErrors(mapFieldErrors(se.fields));
      else if (se && se.code === 'ATTESTATION_REQUIRED') setErrors({ attest: se.message });
      else setProblem(refusalText(se, 'Could not add the property. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={submit} noValidate aria-label="Add a property" data-add-property="">
      <Section>
        <SectionTitle>What kind of place is it?</SectionTitle>
        <KindGroup role="radiogroup" aria-label="What kind of place is it?">
          {LAYOUTS.map(l => (
            <KindCard
              key={l}
              type="button"
              role="radio"
              aria-checked={form.layout === l}
              $checked={form.layout === l}
              data-layout={l}
              onClick={() => set({ layout: l, labels: l === 'multi' ? labelsFor('multi', form.labels) : form.labels })}
            >
              <KindTitle>{LAYOUT_WORDS[l].title}</KindTitle>
              <KindHint>{LAYOUT_WORDS[l].hint}</KindHint>
            </KindCard>
          ))}
        </KindGroup>
        <FieldError id={eid('layout')} message={errors.layout} />
      </Section>

      {form.layout ? (
        <>
          <Section>
            <SectionTitle>Where is it?</SectionTitle>
            <BuildingLocationPicker
              value={location}
              onChange={setLocation}
              onUnresolvedChange={setUnresolved}
              onZoneHint={zone.onZoneHint}
              invalid={!!errors.location}
              manualFields={false}
            />
            <FieldError id={eid('location')} message={errors.location} />
            <FieldRow>
              <Field>
                <Label htmlFor="property-address">Street address</Label>
                <Input
                  id="property-address"
                  value={form.address}
                  maxLength={200}
                  autoComplete="street-address"
                  aria-invalid={!!errors.address}
                  aria-describedby={errors.address ? eid('address') : undefined}
                  onChange={e => {
                    typedAddress.current = true;
                    set({ address: e.target.value });
                  }}
                />
                <FieldError id={eid('address')} message={errors.address} />
              </Field>
              <Field>
                <Label htmlFor="property-city">Town or city</Label>
                <Input
                  id="property-city"
                  value={form.city}
                  maxLength={80}
                  autoComplete="address-level2"
                  aria-invalid={!!errors.city}
                  aria-describedby={errors.city ? eid('city') : undefined}
                  onChange={e => {
                    typedCity.current = true;
                    set({ city: e.target.value });
                  }}
                />
                <FieldError id={eid('city')} message={errors.city} />
              </Field>
            </FieldRow>
            {/* With no map (or a place without a country) the host names the
                country: a listing is in its property's country. */}
            {!location.country_code || pickedCountry.current ? (
              <Field>
                <Label htmlFor="property-country">Country</Label>
                <Select
                  id="property-country"
                  value={location.country_code || ''}
                  onChange={e => {
                    pickedCountry.current = true;
                    const c = STAYS_COUNTRIES.find(x => x.code === e.target.value);
                    setLocation(l => ({ ...l, country_code: c ? c.code : null, country: c ? c.name : null }));
                  }}
                >
                  <option value="">Choose…</option>
                  {STAYS_COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            {zone.zone ? (
              <FieldHint data-zone-note="">
                Times for this property: {zone.zone}
                {zone.pinZone ? '' : ' (from this device until you pick the address)'}
              </FieldHint>
            ) : null}
          </Section>

          {form.layout !== 'whole' ? (
            <Section>
              <SectionTitle>{form.layout === 'apartment' ? 'Which apartment?' : 'Which apartments?'}</SectionTitle>
              <Rows>
                {rows.map((label, i) => (
                  <RowLine key={i}>
                    <Field>
                      <Label htmlFor={`property-unit-${i}`}>
                        {form.layout === 'apartment' ? 'Apartment number or name' : `Apartment ${i + 1}`}
                      </Label>
                      <Input
                        id={`property-unit-${i}`}
                        value={label}
                        maxLength={LABEL_MAX}
                        placeholder={form.layout === 'apartment' ? 'For example 1 Esq' : 'For example 2B'}
                        aria-invalid={!!errors[`units.${i}`]}
                        aria-describedby={errors[`units.${i}`] ? eid(`units.${i}`) : undefined}
                        onChange={e => setLabel(i, e.target.value)}
                      />
                      <FieldError id={eid(`units.${i}`)} message={errors[`units.${i}`]} />
                    </Field>
                    {form.layout === 'multi' && rows.length > 2 ? (
                      <AppButton
                        variant="ghost"
                        size="compact"
                        aria-label={`Remove apartment ${i + 1}`}
                        onClick={() => set({ labels: rows.filter((_, j) => j !== i) })}
                      >
                        Remove
                      </AppButton>
                    ) : null}
                  </RowLine>
                ))}
              </Rows>
              <FieldError id={eid('units')} message={errors.units} />
              {form.layout === 'multi' ? (
                <Inline>
                  <AppButton
                    variant="secondary"
                    size="compact"
                    disabled={rows.length >= MULTI_MAX}
                    onClick={() => set({ labels: [...rows, ''] })}
                  >
                    Add another
                  </AppButton>
                  <AppButton variant="ghost" size="compact" aria-pressed={pasting} onClick={() => setPasting(p => !p)}>
                    Paste a list
                  </AppButton>
                </Inline>
              ) : null}
              {form.layout === 'multi' && pasting ? (
                <Field>
                  <Label htmlFor="property-paste">Apartments, one per line or separated by commas</Label>
                  <PasteArea id="property-paste" value={pasted} onChange={e => setPasted(e.target.value)} />
                  <Inline>
                    <AppButton
                      size="compact"
                      onClick={() => {
                        const list = splitPastedList(pasted);
                        if (list.length) set({ labels: labelsFor('multi', list) });
                        setPasting(false);
                        setPasted('');
                      }}
                    >
                      Use this list
                    </AppButton>
                  </Inline>
                </Field>
              ) : null}
            </Section>
          ) : null}

          {hotel ? (
            <Section data-room-types="">
              <SectionTitle>Room types and rooms</SectionTitle>
              <FieldHint>
                Group your rooms by type, as booking sites sell them. Type room numbers one per line, separated by commas, or
                as a range such as 101-110.
              </FieldHint>
              <Rows>
                {form.roomTypes.map((rt, i) => {
                  const count = splitRoomList(rt.rooms).length;

                  return (
                    <Section key={i} data-room-type={i}>
                      <FieldRow>
                        <Field>
                          <Label htmlFor={`room-type-${i}-name`}>Room type {i + 1}</Label>
                          <Input
                            id={`room-type-${i}-name`}
                            value={rt.name}
                            maxLength={ROOM_TYPE_NAME_MAX}
                            placeholder="For example Double"
                            aria-invalid={!!errors[`room_types.${i}.name`]}
                            aria-describedby={errors[`room_types.${i}.name`] ? eid(`room_types.${i}.name`) : undefined}
                            onChange={e => setType(i, { name: e.target.value })}
                          />
                          <FieldError id={eid(`room_types.${i}.name`)} message={errors[`room_types.${i}.name`]} />
                        </Field>
                        <Field>
                          <Label as="span">Sleeps</Label>
                          <Stepper
                            value={rt.guests}
                            min={1}
                            max={30}
                            label={`guests in room type ${i + 1}`}
                            onChange={g => setType(i, { guests: g })}
                          />
                          <FieldError id={eid(`room_types.${i}.max_guests`)} message={errors[`room_types.${i}.max_guests`]} />
                        </Field>
                      </FieldRow>
                      <Field>
                        <Label htmlFor={`room-type-${i}-rooms`}>Room numbers</Label>
                        <PasteArea
                          id={`room-type-${i}-rooms`}
                          value={rt.rooms}
                          placeholder="For example 101-108, 201"
                          aria-invalid={!!errors[`room_types.${i}.rooms`]}
                          aria-describedby={errors[`room_types.${i}.rooms`] ? eid(`room_types.${i}.rooms`) : `room-type-${i}-count`}
                          onChange={e => setType(i, { rooms: e.target.value })}
                        />
                        <FieldHint id={`room-type-${i}-count`}>{count === 1 ? '1 room' : `${count} rooms`}</FieldHint>
                        <FieldError id={eid(`room_types.${i}.rooms`)} message={errors[`room_types.${i}.rooms`]} />
                      </Field>
                      {form.roomTypes.length > 1 ? (
                        <Inline>
                          <AppButton
                            variant="ghost"
                            size="compact"
                            aria-label={`Remove room type ${i + 1}`}
                            onClick={() => set({ roomTypes: form.roomTypes.filter((_, j) => j !== i) })}
                          >
                            Remove this room type
                          </AppButton>
                        </Inline>
                      ) : null}
                    </Section>
                  );
                })}
              </Rows>
              <Inline>
                <AppButton
                  variant="secondary"
                  size="compact"
                  disabled={form.roomTypes.length >= ROOM_TYPES_MAX}
                  onClick={() => set({ roomTypes: [...form.roomTypes, { ...EMPTY_ROOM_TYPE }] })}
                >
                  Add a room type
                </AppButton>
              </Inline>
              <Small data-room-total="">{roomsInAll === 1 ? '1 room in all' : `${roomsInAll} rooms in all`}</Small>
              <FieldError id={eid('room_types')} message={errors.room_types} />
            </Section>
          ) : null}

          <Section>
            <SectionTitle>What guests see</SectionTitle>
            <Field>
              <Label htmlFor="property-name">
                {hotel ? 'Name of the hotel' : form.layout === 'multi' ? 'Name of the property' : 'Name guests see'}
              </Label>
              <Input
                id="property-name"
                value={form.name}
                maxLength={120}
                placeholder={hotel ? 'For example Hotel Alfama' : 'For example Flores Loft'}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? eid('name') : undefined}
                onChange={e => set({ name: e.target.value })}
              />
              {form.layout === 'multi' ? (
                <FieldHint>Each apartment is listed as this name and its number. You can rename them afterwards.</FieldHint>
              ) : null}
              {hotel ? <FieldHint>Each room is listed as its room type and number, for example Double 101.</FieldHint> : null}
              <FieldError id={eid('name')} message={errors.name} />
            </Field>
            <FieldRow>
              <Field>
                <Label htmlFor="property-check-in">Check-in from</Label>
                <Input
                  id="property-check-in"
                  type="time"
                  value={form.checkIn}
                  aria-invalid={!!errors.check_in_time}
                  onChange={e => set({ checkIn: e.target.value })}
                />
                <FieldError id={eid('check_in_time')} message={errors.check_in_time} />
              </Field>
              <Field>
                <Label htmlFor="property-check-out">Check-out by</Label>
                <Input
                  id="property-check-out"
                  type="time"
                  value={form.checkOut}
                  aria-invalid={!!errors.check_out_time}
                  onChange={e => set({ checkOut: e.target.value })}
                />
                <FieldError id={eid('check_out_time')} message={errors.check_out_time} />
              </Field>
            </FieldRow>
            {hotel ? null : (
              <Field>
                <Label as="span">How many guests it sleeps</Label>
                <Stepper value={form.guests} min={1} max={30} label="guests" onChange={g => set({ guests: g })} />
                <FieldError id={eid('max_guests')} message={errors.max_guests} />
              </Field>
            )}
          </Section>

          {required ? (
            <Section>
              <SectionTitle>Your statement</SectionTitle>
              <Statement htmlFor="property-attest">
                <input
                  id="property-attest"
                  type="checkbox"
                  checked={form.attest}
                  aria-invalid={!!errors.attest}
                  aria-describedby={errors.attest ? eid('attest') : undefined}
                  onChange={e => set({ attest: e.target.checked })}
                />
                <span>{meta.attestation.text}</span>
              </Statement>
              <Small>Wording {meta.attestation.version}. It is kept with the date you ticked it.</Small>
              <FieldError id={eid('attest')} message={errors.attest} />
            </Section>
          ) : null}

          {!meta.capacity.billing ? (
            <Small data-capacity="">
              {room > 0
                ? `Your free preview holds up to ${meta.capacity.cap} places to stay. You can add ${room} more.`
                : `You have used all ${meta.capacity.cap} places to stay in your free preview.`}
              {wants > room && room > 0 ? ` This adds ${wants}.` : ''}
            </Small>
          ) : null}
          {problem ? (
            <FieldError id="property-problem" message={problem} />
          ) : null}
          <Inline>
            <AppButton type="submit" loading={saving} loadingLabel="Adding…">
              Add property
            </AppButton>
          </Inline>
        </>
      ) : null}
    </Form>
  );
};
